// CRM layer — opportunity COMMAND CENTER (Batch 4b: calm enterprise theme).
//
// Same integrated structure and data; restrained visual language — white
// canvas, one indigo accent used sparingly, small soft status pills, thin
// borders, subtle shadows, generous whitespace. No large gradient blocks.
// The transfer action stays REMOVED (it is call-level, not opportunity-level).
//
// Icons: react-icons/fi (lucide-react is not installed; no new library added).

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiArrowRight, FiSend, FiRepeat, FiCornerUpLeft, FiChevronLeft, FiExternalLink,
  FiPhoneIncoming, FiPhoneOutgoing, FiEdit2, FiUser, FiBriefcase, FiTarget,
  FiZap, FiClock, FiActivity, FiMessageSquare, FiPhone, FiCheck
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import {
  crmGetOpportunity, crmGetMessages, crmPostMessage, crmSetStage, crmRevertStage, crmSetDetails,
  crmGetSettings, crmGetClient,
  crmNav, STAGE_LABEL, stageBadgeStyle, nextStage, LADDER, STAGE_RANK, EXIT_STAGES, CLOSED_STAGES,
  fmtDateTime, fmtHours, isPendingClassification, typeLabel, COMMUNICATION_LABEL
} from './crmApi';
import { hasPerm } from './perms';
import useAgentNames from './useAgentNames';
import CrmReassignModal from './CrmReassignModal';
import CrmClassifyGate from './CrmClassifyGate';

const EVENT_LABEL = {
  created: 'إنشاء الطلب', stage_change: 'تغيير المرحلة', stage_revert: 'إرجاع المرحلة',
  assigned: 'إسناد', unassigned: 'سحب الإسناد', reminder: 'تذكير', note: 'ملاحظة',
  classified: 'تصنيف التواصل'
};

const STAGE_GUIDANCE = {
  new: 'أرسل عرضاً للعميل',
  offer_sent: 'تواصل مع العميل وتحقّق من جديته',
  serious: 'اعمل على إغلاق الطلب والتعاقد'
};

// Small, soft urgency pill (no big colored panels).
const GRADE_PILL = {
  green: { label: 'ضمن المهلة', bg: '#ecfdf3', color: '#067647', border: '#abefc6', bar: '#12b76a' },
  amber: { label: 'اقترب موعد التذكير', bg: '#fffaeb', color: '#b54708', border: '#fedf89', bar: '#f79009' },
  red: { label: 'عاجل', bg: '#fef3f2', color: '#b42318', border: '#fecdca', bar: '#f04438' }
};

const CLASSIFICATION_LABEL = { vip: 'عميل مهم', normal: 'عادي', blacklist: 'قائمة سوداء' };
const CALL_STATUS_LABEL = {
  answered: 'مردود عليها', missed: 'فائتة', abandoned: 'أُغلقت',
  no_answer: 'لم يُرد', voicemail: 'بريد صوتي', after_hours: 'خارج الدوام'
};

// Muted avatar palette (calm, low saturation).
const AVATAR_COLORS = ['#6b76c9', '#8b83c9', '#c98ba9', '#c9a878', '#7bb59a', '#78a6c9', '#8a93a8', '#b98aa0'];
const avatarColor = (key) => {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
};
const initials = (name) => {
  const p = String(name || '').trim().split(/\s+/);
  if (!p[0]) return '؟';
  return (p[0][0] || '') + (p[1] ? p[1][0] : '');
};

const fmtMins = (mins) => {
  const m = Math.round(Number(mins));
  if (!Number.isFinite(m) || m <= 0) return 'الآن';
  if (m < 60) return `${m} دقيقة`;
  const h = Math.floor(m / 60), rm = m % 60;
  if (h < 24) return rm ? `${h} ساعة و${rm} دقيقة` : `${h} ساعة`;
  const d = Math.floor(h / 24), rh = h % 24;
  return rh ? `${d} يوم و${rh} ساعة` : `${d} يوم`;
};

const CrmOpportunityPage = ({ setActivePage }) => {
  const { user } = useAuth();
  const { nameOf } = useAgentNames();
  const [id, setId] = useState(crmNav.getOpportunity());
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState([]);
  const [client, setClient] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertStage, setRevertStage] = useState('new');
  const [revertNote, setRevertNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [details, setDetails] = useState('');
  const [showReassign, setShowReassign] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [d, m, s] = await Promise.all([crmGetOpportunity(id), crmGetMessages(id), crmGetSettings()]);
      setData(d.data);
      setMessages(m.data.messages);
      setSettings(s.data.settings || []);
      setDetails(d.data.opportunity.request_details || '');
      const phone = d.data.opportunity.phone_number;
      crmGetClient(phone).then((c) => setClient(c.data)).catch(() => setClient(null));
    } catch (e) {
      setError(e.response?.data?.error || 'تعذر جلب الطلب');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { await fn(); await load(); } catch (e) { setError(e.response?.data?.error || 'فشل الإجراء'); }
  };

  const goOpportunity = (newId) => { crmNav.setOpportunity(newId); setId(String(newId)); };

  if (!data) {
    return (
      <div className="crm-scope crm-opp-page">
        <div className="page-header"><h1 className="page-title">تفاصيل الطلب</h1></div>
        <div className="crm-card2"><div style={{ padding: 44, textAlign: 'center', color: '#98a2b3' }}>
          {error || 'جاري التحميل...'}
        </div></div>
      </div>
    );
  }

  const o = data.opportunity;
  const isAdmin = user?.role === 'admin';

  // ═══════════ BATCH 3: CLASSIFICATION GATE ═══════════
  //
  // A row the auto-scan created is not an opportunity yet, so the command
  // center is replaced entirely until a human says what the call was. Placed
  // before every other computation deliberately: the stepper, timer and stage
  // actions below are all meaningless for a pre-stage row (there is no rung on
  // the ladder, and crm_settings has no window for this stage).
  if (isPendingClassification(o)) {
    return (
      <CrmClassifyGate
        opportunity={o}
        clientName={o.client_name || (client && client.client && client.client.full_name)}
        agentName={nameOf(o.assigned_to_extension)}
        calls={data.calls || []}
        // Mirrors the backend rule (assigned agent or admin); the server
        // re-checks and returns 403, so this only decides what to render.
        canClassify={o.assigned_to_extension === user?.extension || isAdmin}
        onClassified={(choice, archived) => {
          if (archived) {
            // The three archiving answers close the row, so there is nothing
            // left to look at — confirm on the board instead.
            crmNav.setFlash(`تم التصنيف والأرشفة — ${COMMUNICATION_LABEL[choice]}`);
            setActivePage('crm-board');
          } else {
            // 'real': the row is now a normal opportunity at stage "new".
            // Reloading drops straight into the journey UI below.
            load();
          }
        }}
        onBack={() => setActivePage('crm-board')}
      />
    );
  }

  const canRevert = hasPerm(user, 'revert_stage');
  const canAssign = hasPerm(user, 'assign_opportunity');
  const up = nextStage(o.crm_stage);
  const closed = CLOSED_STAGES.includes(o.crm_stage);
  const exited = EXIT_STAGES.includes(o.crm_stage);
  const canEdit = o.assigned_to_extension === user?.extension || isAdmin;
  const rank = exited ? -1 : STAGE_RANK[o.crm_stage];
  const fillPct = exited ? 0 : (rank / (LADDER.length - 1)) * 100;

  // ---- timer / urgency from crm_settings ----
  const stageSetting = settings.find((s) => s.stage_key === o.crm_stage);
  let timer = null;
  if (stageSetting && !closed) {
    const max = stageSetting.max_reminders;
    const unassignMin = stageSetting.unassign_after_minutes;
    const windowMin = stageSetting.effective_window_minutes;
    const elapsedMin = Math.max(0, (Date.now() - new Date(o.stage_changed_at).getTime()) / 60000);
    const remaining = Math.max(0, max - o.reminder_count);
    const untilUnassign = Math.max(0, unassignMin - elapsedMin);
    const barPct = unassignMin > 0 ? Math.min(100, Math.round((elapsedMin / unassignMin) * 100)) : 0;
    let grade = 'green';
    if (o.reminder_count >= max) grade = 'red';
    else if (o.reminder_count >= 1 || elapsedMin >= windowMin) grade = 'amber';
    timer = { max, remaining, untilUnassign, grade, barPct };
  }

  const guidance = STAGE_GUIDANCE[o.crm_stage];
  const cp = client && client.client;
  const otherOpps = (client && client.opportunities ? client.opportunities : []).filter((x) => x.id !== o.id);
  const calls = data.calls || [];
  const agentName = nameOf(o.assigned_to_extension);

  return (
    <div className="crm-scope crm-opp-page">
      {/* ═══════════ CLEAN HEADER CARD ═══════════ */}
      <div className="crm-hero">
        <div className="crm-hero-top">
          <div>
            <h1>{typeLabel(o.opportunity_type)}</h1>
            <div className="crm-hero-type">{o.client_name || 'عميل غير مسجل'}</div>
            <span className="crm-hero-pill"><FiTarget size={12} /> {STAGE_LABEL[o.crm_stage]}</span>
          </div>
          <div className="crm-hero-actions">
            <button className="crm-ghost-btn"
              onClick={() => { crmNav.setClient(o.phone_number); setActivePage('crm-client'); }}>
              <FiArrowRight size={14} /> الملف الكامل للعميل
            </button>
            <button className="crm-ghost-btn" onClick={() => setActivePage('crm-board')}>كل الفرص</button>
          </div>
        </div>
        <div className="crm-hero-meta">
          <span className="item"><FiPhone size={13} className="lc" /><b style={{ direction: 'ltr', display: 'inline-block' }}>{o.phone_number}</b></span>
          <span className="item">
            <span className="crm-avatar" style={{ width: 22, height: 22, fontSize: 10, background: agentName ? avatarColor(o.assigned_to_extension) : '#cbd2da' }}>
              {agentName ? initials(agentName) : '?'}
            </span>
            <b>{agentName || 'غير مسند'}</b>
          </span>
          <span className="item"><FiBriefcase size={13} className="lc" /><b>{o.department || '—'}</b></span>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef3f2', color: '#b42318', padding: '11px 15px', borderRadius: 11, marginBottom: 14, fontSize: 13, fontWeight: 600, border: '1px solid #fecdca' }}>
          {error}
        </div>
      )}

      {/* ═══════════ STEPPER + GUIDANCE + TIMER ═══════════ */}
      <div className="crm-card2">
        <div className="crm-card2-body">
          <div className={`crm-steps${exited ? ' exited' : ''}`}>
            <div className="crm-track"><div className="crm-track-fill" style={{ width: `${fillPct}%` }} /></div>
            {LADDER.map((s, i) => {
              const done = !exited && STAGE_RANK[s] < rank;
              const here = !exited && s === o.crm_stage;
              const isNext = s === up;
              const cls = done ? 'is-done' : here ? 'is-here' : '';
              return (
                <div key={s} className={`crm-step2 ${cls}`}>
                  <div className={`crm-node ${done ? 'done' : here ? 'here' : isNext ? 'next' : ''}`}>
                    {done ? <FiCheck size={18} /> : i + 1}
                  </div>
                  <div className="lbl">{STAGE_LABEL[s]}</div>
                </div>
              );
            })}
          </div>

          {closed ? (
            <div className="crm-cta closed">
              <span className="ico"><FiTarget size={16} /></span>
              <span><span className="tag">النتيجة</span><b>{STAGE_LABEL[o.crm_stage]}</b> — هذا الطلب مغلق ولا يظهر ضمن الفرص النشطة.</span>
            </div>
          ) : guidance ? (
            <div className="crm-cta">
              <span className="ico"><FiZap size={16} /></span>
              <span><span className="tag">المطلوب الآن</span>{guidance}</span>
            </div>
          ) : null}

          {timer && (() => {
            const g = GRADE_PILL[timer.grade];
            return (
              <div className="crm-timer2">
                <div className="trow">
                  <div>
                    <div className="lead"><FiClock size={12} style={{ verticalAlign: 'middle', marginLeft: 4 }} />في هذه المرحلة منذ</div>
                    <div className="big">{fmtHours(o.hoursInStage)}</div>
                  </div>
                  <span className="crm-upill" style={{ background: g.bg, color: g.color, borderColor: g.border }}>{g.label}</span>
                </div>
                <div className="crm-dots2">
                  <span className="lbl">التذكيرات {o.reminder_count} / {timer.max}</span>
                  {Array.from({ length: timer.max }).map((_, k) => (
                    <span key={k} className={`dot${k < o.reminder_count ? ' on' : ''}`} />
                  ))}
                </div>
                <div className="crm-ubar"><span style={{ width: `${timer.barPct}%`, background: g.bar }} /></div>
                <div className="sub">
                  {timer.remaining > 0
                    ? `يتبقى ${timer.remaining} تذكير · سحب الإسناد التلقائي بعد نحو ${fmtMins(timer.untilUnassign)}`
                    : 'أُرسلت كل التذكيرات — سيُسحب الإسناد في أقرب دورة للنظام'}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ═══════════ TWO COLUMNS ═══════════ */}
      <div className="crm-cmd-cols">
        {/* RIGHT (start): actions + details + client */}
        <div>
          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiZap size={15} /></span>
              <h3>الإجراءات</h3>
            </div>
            <div className="crm-card2-body">
              <div className="crm-actstack2">
                {!closed && up && (
                  <button className="crm-btn crm-btn-primary" onClick={() => act(() => crmSetStage(o.id, up))}>
                    <FiChevronLeft size={15} /> الانتقال إلى: {STAGE_LABEL[up]}
                  </button>
                )}
                {canAssign && (
                  <button className="crm-btn crm-btn-soft" onClick={() => setShowReassign(true)}>
                    <FiRepeat size={14} /> إعادة إسناد
                  </button>
                )}
                {canRevert && (
                  <button className="crm-btn crm-btn-rose" onClick={() => setRevertOpen(!revertOpen)}>
                    <FiCornerUpLeft size={14} /> إرجاع المرحلة
                  </button>
                )}
                {!closed && (
                  <div className="crm-btn-row">
                    {EXIT_STAGES.map((s) => (
                      <button key={s} className="crm-btn crm-btn-rose" onClick={() => act(() => crmSetStage(o.id, s))}>
                        {STAGE_LABEL[s]}
                      </button>
                    ))}
                  </div>
                )}
                {closed && <span className="crm-hint">هذا الطلب مغلق — لا إجراءات على مرحلته.</span>}
              </div>

              {canRevert && revertOpen && (
                <div className="crm-revert-panel">
                  <label style={{ fontSize: 12.5, color: '#9f1239', fontWeight: 700, display: 'block', marginBottom: 4 }}>المرحلة الجديدة</label>
                  <select className="filter-select" style={{ width: '100%', marginBottom: 10 }} value={revertStage}
                    onChange={(e) => setRevertStage(e.target.value)}>
                    {Object.keys(STAGE_LABEL).map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                  </select>
                  <label style={{ fontSize: 12.5, color: '#9f1239', fontWeight: 700, display: 'block', marginBottom: 4 }}>سبب الإرجاع (إجباري)</label>
                  <input className="filter-input" style={{ width: '100%', marginBottom: 10 }} value={revertNote}
                    onChange={(e) => setRevertNote(e.target.value)} />
                  <button className="crm-btn crm-btn-rose" style={{ width: 'auto', padding: '9px 16px' }}
                    onClick={() => act(async () => { await crmRevertStage(o.id, revertStage, revertNote); setRevertOpen(false); setRevertNote(''); })}>
                    تنفيذ الإرجاع
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiActivity size={15} /></span>
              <h3>تفاصيل الطلب</h3>
              <span style={{ flex: 1 }} />
              {canEdit
                ? <button className="crm-btn crm-btn-soft" style={{ width: 'auto', padding: '6px 11px', fontSize: 12.5 }} onClick={() => setEditing(!editing)}>
                    <FiEdit2 size={12} /> {editing ? 'إلغاء' : 'تعديل'}
                  </button>
                : <span className="crm-hint">للموظف المسؤول أو المدير</span>}
            </div>
            <div className="crm-card2-body">
              {editing ? (
                <div>
                  <textarea className="filter-input" style={{ width: '100%', minHeight: 100 }}
                    value={details} onChange={(e) => setDetails(e.target.value)} />
                  <button className="crm-btn crm-btn-primary" style={{ width: 'auto', padding: '9px 18px', marginTop: 10 }}
                    onClick={() => act(async () => { await crmSetDetails(o.id, details); setEditing(false); })}>حفظ</button>
                </div>
              ) : (
                <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, color: o.request_details ? '#1f2430' : '#98a2b3', margin: '0 0 16px' }}>
                  {o.request_details || 'لا توجد تفاصيل مسجلة.'}
                </p>
              )}
              <div className="crm-kv2" style={{ paddingTop: 16, borderTop: '1px solid #f2f3f5' }}>
                <div><span className="k">عدد التذكيرات</span><span className="v">{o.reminder_count}</span></div>
                <div><span className="k">دخول المرحلة</span><span className="v" style={{ fontSize: 13 }}>{fmtDateTime(o.stage_changed_at)}</span></div>
                <div><span className="k">تاريخ الإنشاء</span><span className="v" style={{ fontSize: 13 }}>{fmtDateTime(o.created_at)}</span></div>
                <div><span className="k">القسم</span><span className="v">{o.department || '—'}</span></div>
                {/* Batch 3: only auto-created rows carry a classification. */}
                {o.communication_type && (
                  <div>
                    <span className="k">تصنيف التواصل</span>
                    <span className="v">{COMMUNICATION_LABEL[o.communication_type] || o.communication_type}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiUser size={15} /></span>
              <h3>ملف العميل</h3>
              <span style={{ flex: 1 }} />
              <button className="crm-btn crm-btn-soft" style={{ width: 'auto', padding: '6px 11px', fontSize: 12.5 }}
                onClick={() => { crmNav.setClient(o.phone_number); setActivePage('crm-client'); }}>
                <FiExternalLink size={12} /> الملف الكامل
              </button>
            </div>
            <div className="crm-card2-body">
              <div className="crm-kv2">
                <div><span className="k">الاسم</span><span className="v">{(cp && cp.full_name) || o.client_name || 'غير مسجل'}</span></div>
                <div><span className="k">التصنيف</span><span className="v">{cp && cp.classification ? (CLASSIFICATION_LABEL[cp.classification] || cp.classification) : '—'}</span></div>
                <div><span className="k">المدينة</span><span className="v">{(cp && cp.city) || '—'}</span></div>
                <div><span className="k">البريد</span><span className="v" style={{ direction: 'ltr', fontSize: 13 }}>{(cp && cp.email) || '—'}</span></div>
              </div>
              {otherOpps.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f2f3f5' }}>
                  <div style={{ fontSize: 12, color: '#98a2b3', fontWeight: 600, marginBottom: 8 }}>طلبات أخرى لهذا العميل ({otherOpps.length})</div>
                  {otherOpps.map((x) => (
                    <div key={x.id} className="crm-mini2" onClick={() => goOpportunity(x.id)}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{typeLabel(x.opportunity_type)}</span>
                      {isPendingClassification(x)
                        ? <span className="crm-pill crm-pill-pending">{STAGE_LABEL[x.crm_stage]}</span>
                        : <span className="crm-badge" style={stageBadgeStyle(x.crm_stage)}>{STAGE_LABEL[x.crm_stage]}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LEFT: history + communication */}
        <div>
          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiPhone size={15} /></span>
              <h3>مكالمات العميل</h3>
              <span style={{ flex: 1 }} />
              <span className="count">{calls.length} مكالمة</span>
            </div>
            <div className="crm-card2-body" style={{ paddingTop: 6, paddingBottom: 6 }}>
              {calls.length === 0 ? (
                <div style={{ color: '#98a2b3', fontSize: 13, textAlign: 'center', padding: '22px 0' }}>لا توجد مكالمات مسجلة</div>
              ) : calls.slice(0, 20).map((c) => {
                const out = c.direction === 'outbound';
                return (
                  <div key={c.id} className="crm-call2">
                    <span className="cico">{out ? <FiPhoneOutgoing size={13} /> : <FiPhoneIncoming size={13} />}</span>
                    <span className="crm-badge" style={{ background: '#f1f3f7', color: '#475467' }}>{CALL_STATUS_LABEL[c.status] || c.status}</span>
                    <span style={{ color: '#475467', fontWeight: 600 }}>{c.answered_by ? (nameOf(c.answered_by_extension) || c.answered_by) : '—'}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: '#98a2b3', fontSize: 12 }}>{fmtDateTime(c.started_at)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiActivity size={15} /></span>
              <h3>سجل الأحداث</h3>
              <span style={{ flex: 1 }} />
              <span className="count">{data.events.length} حدث</span>
            </div>
            <div className="crm-card2-body" style={{ paddingTop: 6, paddingBottom: 6 }}>
              {data.events.length === 0 ? (
                <div style={{ color: '#98a2b3', fontSize: 13, textAlign: 'center', padding: '22px 0' }}>لا توجد أحداث</div>
              ) : (
                <div className="crm-tl">
                  {data.events.map((e) => (
                    <div key={e.id} className={`crm-tl-item e-${e.event_type}`}>
                      <span className="crm-tl-dot" />
                      <div>
                        <div className="crm-tl-title">
                          {EVENT_LABEL[e.event_type] || e.event_type}
                          {(e.from_value || e.to_value) && (
                            <span style={{ color: '#98a2b3', fontWeight: 400 }}>
                              {' — '}
                              {e.from_value ? `${STAGE_LABEL[e.from_value] || nameOf(e.from_value) || e.from_value} ← ` : ''}
                              {STAGE_LABEL[e.to_value] || nameOf(e.to_value) || e.to_value || ''}
                            </span>
                          )}
                        </div>
                        <div className="crm-tl-sub">بواسطة {nameOf(e.by_extension) || 'النظام'}</div>
                      </div>
                      <div className="crm-tl-when">{fmtDateTime(e.created_at)}</div>
                      {e.note && <div className="crm-tl-note">{e.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="crm-card2">
            <div className="crm-card2-head">
              <span className="hicon"><FiMessageSquare size={15} /></span>
              <h3>المحادثة الداخلية</h3>
              <span style={{ flex: 1 }} />
              <span className="count">{messages.length} رسالة</span>
            </div>
            <div className="crm-card2-body">
              <div className="crm-chat">
                {messages.length === 0 ? (
                  <div style={{ color: '#98a2b3', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>لا توجد رسائل بعد</div>
                ) : messages.map((m) => {
                  const mine = m.by_extension === user?.extension;
                  const who = m.by_name || nameOf(m.by_extension) || m.by_extension;
                  return (
                    <div key={m.id} className={`crm-msg ${mine ? 'mine' : 'other'}`}>
                      <span className="crm-avatar" style={{ background: mine ? '#5b54d6' : avatarColor(m.by_extension) }}>{initials(who)}</span>
                      <div>
                        {!mine && <div className="who">{who} · {fmtDateTime(m.created_at)}</div>}
                        <div className="bubble">{m.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="crm-chat-input">
                <input className="filter-input" style={{ flex: 1 }} placeholder="اكتب رسالة للفريق..."
                  value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) act(async () => { await crmPostMessage(o.id, draft); setDraft(''); }); }} />
                <button className="crm-send-btn" disabled={!draft.trim()}
                  onClick={() => act(async () => { await crmPostMessage(o.id, draft); setDraft(''); })}>
                  <FiSend size={13} /> إرسال
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showReassign && (
        <CrmReassignModal
          opportunityId={o.id}
          currentExtension={o.assigned_to_extension}
          onClose={() => setShowReassign(false)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default CrmOpportunityPage;
